import { useEffect, useState } from "react";
import { Link, Navigate, NavLink, Outlet } from "react-router-dom";
import { Loader2, LogOut } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "./AuthContext";
import { Staff } from "./types";
import TeamDialog from "./TeamDialog";
import { AccMark } from "@/components/site/AccMark";
import CalendarConnect from "./CalendarConnect";

export type ShellCtx = { staff: Staff[]; reloadStaff: () => void };

export default function CrmShell() {
  const { user, logout } = useAuth();
  const [staff, setStaff] = useState<Staff[]>([]);
  const reloadStaff = () => { api.get("/staff").then((r) => setStaff(r.data)); };

  useEffect(() => {
    document.title = "Lead Desk | All Concept Concrete";
    let m = document.head.querySelector<HTMLMetaElement>('meta[name="robots"]');
    if (!m) { m = document.createElement("meta"); m.name = "robots"; document.head.appendChild(m); }
    m.content = "noindex, nofollow";
    if (user) reloadStaff();
  }, [user]);

  if (user === null) return <div className="grid min-h-screen place-items-center bg-[#0F1115]"><Loader2 className="h-6 w-6 animate-spin text-brand-500" /></div>;
  if (user === false) return <Navigate to="/staff/login" replace />;

  return (
    <div className="min-h-screen bg-[#0F1115] text-slate-200">
      <header className="sticky top-0 z-40 border-b border-slate-800 bg-[#0F1115]/90 backdrop-blur-xl">
        <div className="flex h-14 items-center justify-between px-4 md:px-6">
          <Link to="/staff" className="flex items-center gap-2" data-testid="crm-home-link">
            <AccMark className="h-7 w-auto" />
            <span className="hidden font-display text-lg font-bold uppercase tracking-wide text-white sm:inline">Lead Desk</span>
          </Link>
          <nav className="flex items-center gap-1">
            {[["/staff", "Leads", "crm-nav-leads"], ["/staff/content", "Website", "crm-nav-content"]].map(([to, label, id]) => (
              <NavLink key={to} to={to} end data-testid={id}
                className={({ isActive }) => `rounded-sm px-2.5 py-1.5 text-xs uppercase tracking-wide ${isActive ? "bg-brand-500/15 text-brand-300" : "text-slate-400 hover:text-white"}`}>
                {label}
              </NavLink>
            ))}
          </nav>
          <div className="flex items-center gap-2">
            <CalendarConnect />
            {user.role === "admin" && <TeamDialog staff={staff} onAdded={reloadStaff} />}
            <span className="hidden text-sm text-slate-400 sm:inline" data-testid="crm-user-name">{user.name}</span>
            <button onClick={logout} data-testid="crm-logout-button" className="flex items-center gap-1 rounded-sm border border-slate-700 px-2.5 py-1.5 text-xs text-slate-300 hover:border-brand-500 hover:text-brand-400">
              <LogOut className="h-3.5 w-3.5" /> Log out
            </button>
          </div>
        </div>
      </header>
      <Outlet context={{ staff, reloadStaff } satisfies ShellCtx} />
    </div>
  );
}
